import { ORDER_NUMBER, TradingCharacteristicsEnum } from '../../../../../constants';
import { ArrayHelpers, FieldArray, FormikProps, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { FaPlusCircle, FaTrash } from 'react-icons/fa';
import { FinalTermType } from '../../../app';
import { createSecurity } from '../helpers';
import { SecurityFields, SecurityFieldsType } from '../security';
import { TooltipText } from 'components/form/tooltiptext';

export type SecurityListType = SecurityFieldsType[];

export type SecurityListProps = {
  controlId: string;
};

export const SecurityList: React.FC<React.PropsWithChildren<SecurityListProps>> = ({ controlId }) => {
  const [activeId, setActiveId] = useState(0);
  const [deletedRow, setDeletedRow] = useState(-1);
  const { values, setFieldValue } = useFormikContext<FinalTermType>();

  function changeActiveId(index: number) {
    if (index === activeId) {
      setActiveId(-1);
    } else {
      setActiveId(index);
    }
  }

  function handleRemoveOnIcon(event: React.MouseEvent<SVGElement, MouseEvent>, index: number, callback: any) {
    event.preventDefault();
    event.stopPropagation();

    if (activeId > index) {
      setActiveId(activeId - 1);
    } else if (activeId === index) {
      setActiveId(-1);
    }

    callback(index);
    setDeletedRow(index);
  }

  function handleAddNewSecurity(lastIndex: number, callback: any) {
    callback(createSecurity(lastIndex + 1));

    changeActiveId(lastIndex);
  }

  useEffect(() => {
    if (values.securities.length > 0 && deletedRow >= 0) {
      values.securities.forEach((security, index) => {
        setFieldValue(`${controlId}[${index}]${ORDER_NUMBER}`, index + 1, false);
      });
      setDeletedRow(-1);
    }
  }, [values, controlId, setFieldValue, deletedRow]);

  return (
    <div className="security-list">
      <FieldArray name={controlId}>
        {({ form: { values }, remove, push }: { form: FormikProps<FinalTermType> } & ArrayHelpers) => (
          <>
            {values.securities.length === 0 ? (
              <Alert variant="danger">
                <div>At least one security must be reported.</div>
              </Alert>
            ) : (
              values.securities.map((security, index) => (
                <Row key={index} className="security-row">
                  <Col xs={12} className="security-header">
                    <Row onClick={() => changeActiveId(index)}>
                      <Col xs={1}>{security.orderNumber}</Col>
                      <Col>{security.fisn}</Col>
                      <Col>{security.isin}</Col>
                      <Col>Offer: {security.offerToPublic ? 'yes' : 'no'}</Col>
                      <Col xs={1}>
                        <TooltipText memberStates={security.memberStateOfferToPublic}></TooltipText>
                      </Col>
                      <Col xs={3}>
                        Admission to trading:{' '}
                        {security.tradingVenueCharacteristics
                          ? [TradingCharacteristicsEnum.RMKT.toString(), TradingCharacteristicsEnum.RMQI.toString()].includes(
                              security.tradingVenueCharacteristics
                            )
                            ? 'RM'
                            : [TradingCharacteristicsEnum.MLTF.toString(), TradingCharacteristicsEnum.MSGM.toString()].includes(
                                  security.tradingVenueCharacteristics
                                )
                              ? 'MTF'
                              : 'no'
                          : 'no'}
                      </Col>
                      <Col xs={1}>
                        <TooltipText memberStates={security.memberStateAdmissionToTrading}></TooltipText>
                      </Col>
                      <Col className="text-end">
                        <FaTrash className="delete-icon" onClick={(event) => handleRemoveOnIcon(event, index, remove)}></FaTrash>
                      </Col>
                    </Row>
                  </Col>
                  {activeId === index && (
                    <Col xs={12} className="security-content">
                      <SecurityFields controlId={controlId} index={index} />
                    </Col>
                  )}
                </Row>
              ))
            )}
            <Row>
              <Col className="text-center">
                <FaPlusCircle className="add-icon" onClick={() => handleAddNewSecurity(values.securities.length, push)}></FaPlusCircle>
              </Col>
            </Row>
          </>
        )}
      </FieldArray>
    </div>
  );
};
